import { Directive, inject, output } from '@angular/core';
import { CachedCurrentUserService, OrgSwitcherItem } from '@pure/pure1-ui-platform-angular';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { EffectiveOrgService } from '../../../data/services/effective-org.service';
import { OrgSwitcherService } from './org-switcher.service';

/** A directive, not a wrapper component: Hive styles the switcher as a direct child of the rail header. */
@Directive({
    selector: 'hive-org-switcher[p1mOrgSwitcher]',
    exportAs: 'p1mOrgSwitcher',
    host: {
        '(selectionChange)': 'onSelectionChange($event)',
        '(filterChange)': 'onFilterChange($event)',
        '(tryAgainClick)': 'service.loadOrgSwitcherItems()',
    },
})
export class OrgSwitcherDirective {
    readonly switchError = output<{ message: string; cause: unknown }>();

    readonly service = inject(OrgSwitcherService);
    private readonly effectiveOrgService = inject(EffectiveOrgService);
    private readonly currentUserservice = inject(CachedCurrentUserService);

    readonly filterDebounceWait = 0;
    private readonly filter$ = new BehaviorSubject('');

    readonly activeOrg$ = combineLatest([this.effectiveOrgService.effectiveOrg$, this.service.userOrgs$]).pipe(
        map(([effectiveOrg, userOrgs]) =>
            effectiveOrg ? (userOrgs.find(o => o.uid === effectiveOrg.uid) ?? null) : null,
        ),
    );
    readonly isLoading$ = this.service.isLoading$;
    readonly hasLoadingError$ = this.service.hasLoadingError$;
    readonly orgLabel$ = this.activeOrg$.pipe(map(org => org?.name ?? ''));
    readonly companyLabel$ = this.activeOrg$.pipe(map(org => org?.opCompany?.name));

    readonly groups$ = combineLatest([
        this.service.userOrgs$,
        this.filter$,
        this.currentUserservice.getCurrentUser(),
    ]).pipe(
        map(([orgs, filter, user]) => {
            const filteredOrgs = filter ? orgs.filter(o => o.name.toLowerCase().includes(filter.toLowerCase())) : orgs;

            return [
                {
                    name: 'My Organization',
                    items: filteredOrgs.filter(org => org.opCompany.uid === user.operatingCompany.uid),
                },
                {
                    name: 'External Organizations',
                    items: filteredOrgs.filter(org => org.opCompany.uid !== user.operatingCompany.uid),
                },
            ].filter(group => group.items.length > 0);
        }),
    );

    protected onFilterChange(term: string): void {
        this.filter$.next(term);
    }

    protected async onSelectionChange(item: OrgSwitcherItem): Promise<void> {
        try {
            await this.service.switch(item);
        } catch (cause) {
            this.switchError.emit({ message: 'Failed to switch organization', cause });
        }
    }
}
